import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '@/contexts/AuthContext';
import ReassignRow from '@/components/ReassignRow';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A', slate: '#5C6B7A', bg: '#F4F6F9' };
const MIN_REASON = 12;

export default function StaleReassignPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [busy, setBusy] = useState(false);
  const [bulkHandler, setBulkHandler] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [result, setResult] = useState(null);

  const load = useCallback(() => api.get('/claims/stale/reassign-queue').then(r => { setData(r.data); setDecisions({}); }).catch(() => setData({ claims: [], handlers: [], count: 0 })), [api]);
  useEffect(() => { load(); }, [load]);

  const set = (id, patch) => setDecisions(d => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));
  const ready = useMemo(() => Object.entries(decisions).filter(([, d]) => d.action && (d.reason || '').trim().length >= MIN_REASON && (d.action !== 'reassign' || d.handler_id)), [decisions]);
  const incomplete = Object.values(decisions).filter(d => d.action).length - ready.length;

  const applyBulk = (action) => {
    if (!data) return;
    const patch = { action, reason: bulkReason };
    if (action === 'reassign') { if (!bulkHandler) { toast.error('Pick a handler for the bulk reassign'); return; } patch.handler_id = bulkHandler; }
    setDecisions(d => { const n = { ...d }; data.claims.forEach(c => { if (!n[c.claim_id]?.action) n[c.claim_id] = { ...(n[c.claim_id] || {}), ...patch }; }); return n; });
  };

  const submit = async () => {
    if (!ready.length) return;
    setBusy(true);
    try {
      const r = await api.post('/claims/stale/reassign-sweep', { decisions: ready.map(([claim_id, d]) => ({ claim_id, action: d.action, handler_id: d.handler_id, reason: d.reason.trim() })) });
      setResult(r.data);
      toast.success(`${r.data.applied} file(s) decided — ${r.data.by_action.reassign} reassigned, ${r.data.by_action.close} closed, ${r.data.by_action.keep} kept`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(Array.isArray(detail) ? `${detail.length} row(s) rejected — ${detail[0]?.error}` : (detail || 'Sweep failed'));
    } finally { setBusy(false); }
  };

  if (!data) return <div className="p-6 text-sm" style={{ color: C.slate }} data-testid="reassign-loading">Loading the escalated files…</div>;

  return (
    <div className="space-y-4" data-testid="reassign-sweep-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base md:text-lg font-semibold" style={{ color: C.navy }}>Reassign sweep — {data.count} escalated stale file(s)</h2>
          <p className="text-[11px] mt-1 max-w-3xl" style={{ color: C.slate }}>{data.basis}</p>
        </div>
        <div className="flex items-center gap-2 text-[11px]" style={{ color: C.slate }} data-testid="reassign-roster">
          <Users className="w-3.5 h-3.5" />
          {data.handlers.map(h => <span key={h.id} className="px-1.5 py-0.5 rounded-sm" style={{ background: C.bg }} data-testid={`roster-${h.id}`}>{h.name} · {h.open_files}</span>)}
        </div>
      </div>

      {data.count > 0 && (
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-sm" style={{ background: C.bg }} data-testid="reassign-bulk-bar">
          <span className="text-[11px] font-semibold" style={{ color: C.navy }}>Fill the undecided rows:</span>
          <select value={bulkHandler} onChange={e => setBulkHandler(e.target.value)} className="text-[11px] border rounded-sm px-2 py-1 bg-white" data-testid="bulk-handler-select">
            <option value="">handler…</option>
            {data.handlers.map(h => <option key={h.id} value={h.id}>{h.name} ({h.level}, {h.open_files} open)</option>)}
          </select>
          <input value={bulkReason} onChange={e => setBulkReason(e.target.value)} placeholder={`reason for every row (min ${MIN_REASON} chars)`} className="text-[11px] border rounded-sm px-2 py-1 bg-white w-72" data-testid="bulk-reason-input" />
          <button onClick={() => applyBulk('reassign')} className="text-[11px] px-2 py-1 rounded-sm text-white" style={{ background: C.blue }} data-testid="bulk-reassign-btn">Reassign all undecided</button>
          <button onClick={() => applyBulk('close')} className="text-[11px] px-2 py-1 rounded-sm text-white" style={{ background: C.red }} data-testid="bulk-close-btn">Close all undecided</button>
          <button onClick={() => setDecisions({})} className="text-[11px] underline" style={{ color: C.slate }} data-testid="bulk-clear-btn">clear</button>
        </div>
      )}

      {data.count === 0 ? (
        <div className="flex items-center gap-2 p-6 rounded-sm" style={{ background: C.bg }} data-testid="reassign-empty">
          <CheckCircle2 className="w-4 h-4" style={{ color: C.green }} />
          <span className="text-sm" style={{ color: C.navy }}>No escalated stale files — every open claim is with a person.</span>
        </div>
      ) : (
        <div className="overflow-x-auto bg-white border rounded-sm" style={{ borderColor: 'rgba(136,150,168,0.3)' }}>
          <table className="w-full text-[11px]" data-testid="reassign-table">
            <thead><tr style={{ color: C.slate, background: C.bg }}>
              <th className="text-left px-3 py-2">claim</th><th className="text-left">insured · status</th><th className="text-left">handler</th>
              <th className="text-right">idle</th><th className="text-right">reserve</th><th className="text-left pl-3">last human note</th>
              <th className="text-left pl-3 w-28">decision</th><th className="text-left w-44">to handler</th><th className="text-left w-72">reason</th>
            </tr></thead>
            <tbody>{data.claims.map(c => <ReassignRow key={c.claim_id} claim={c} handlers={data.handlers} decision={decisions[c.claim_id] || {}} onChange={(p) => set(c.claim_id, p)} />)}</tbody>
          </table>
        </div>
      )}

      {data.count > 0 && (
        <div className="flex items-center justify-between sticky bottom-0 bg-white border-t py-3" style={{ borderColor: 'rgba(136,150,168,0.3)' }}>
          <p className="text-[11px]" style={{ color: incomplete ? C.amber : C.slate }} data-testid="reassign-readiness">
            {ready.length} of {data.count} decided{incomplete ? ` · ${incomplete} row(s) need a handler or a longer reason` : ''}
          </p>
          <button onClick={submit} disabled={busy || !ready.length} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-sm text-white disabled:opacity-50" style={{ background: C.navy }} data-testid="reassign-apply-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Apply {ready.length} decision(s)
          </button>
        </div>
      )}

      {result && (
        <p className="text-[11px]" style={{ color: C.green }} data-testid="reassign-result">
          Last sweep: {result.applied} applied ({result.by_action.reassign} reassigned, {result.by_action.close} closed, {result.by_action.keep} kept){result.failed ? ` · ${result.failed} failed` : ''}
        </p>
      )}
    </div>
  );
}
